import { addGhostCover, removeGhostCover, reorderGhostCovers, searchGhostCovers } from './api.js';
import { SEARCH_DEBOUNCE_MS, SEARCH_LIMIT, SEARCH_MIN_LENGTH } from './constants.js';
import { resolveAdminGhostCoversDom } from './dom.js';
import { isRecord } from './helpers.js';

const PREVIEW_CELL_COUNT = 48;
const RECOMMENDED_POOL_MIN = 12;
const RECOMMENDED_POOL_MAX = 20;

export function initAdminGhostCoversPage() {
    const dom = resolveAdminGhostCoversDom();
    if (dom === null) {
        return;
    }

    const {
        addUrl,
        coverCardTemplate,
        csrfToken,
        empty,
        guidance,
        grid,
        previewEmpty,
        previewGrid,
        reorderUrl,
        searchInput,
        searchResults,
        searchResultTemplate,
        searchStatus,
        searchUrl,
        total,
    } = dom;

    let searchDebounceId = null;
    let searchSequence = 0;
    let mutationInFlight = false;
    let dragSourceCard = null;
    let dragStartOrderIds = [];
    let dragDidReorder = false;

    const setSearchStatus = (message, tone = 'info') => {
        searchStatus.textContent = message;
        searchStatus.dataset.tone = tone;
    };

    const getCoverCards = () => Array.from(grid.querySelectorAll('.admin-ghost-cover-card')).filter((card) => {
        return card instanceof HTMLElement;
    });

    const getCoverOrderIds = () => getCoverCards().map((card) => {
        return String(card.dataset.ghostCoverId ?? '').trim();
    }).filter((id) => id !== '');

    const getCoverPath = (card) => {
        const image = card.querySelector('[data-ghost-cover-image]');
        if (!(image instanceof HTMLImageElement)) {
            return '';
        }

        return image.src.trim();
    };

    const areOrdersEqual = (left, right) => {
        if (left.length !== right.length) {
            return false;
        }

        for (let index = 0; index < left.length; index += 1) {
            if (left[index] !== right[index]) {
                return false;
            }
        }

        return true;
    };

    const getAddButtons = () => Array.from(searchResults.querySelectorAll('[data-ghost-search-add]')).filter((button) => {
        return button instanceof HTMLButtonElement;
    });

    const syncAddButtons = () => {
        getAddButtons().forEach((button) => {
            const canAdd = button.dataset.canAdd === '1';
            button.disabled = mutationInFlight || !canAdd;
        });
    };

    const updateTotal = () => {
        total.textContent = String(getCoverCards().length);
    };

    const updateGuidance = () => {
        const count = getCoverCards().length;
        const missingCount = Math.max(0, RECOMMENDED_POOL_MIN - count);
        const overflowCount = Math.max(0, count - RECOMMENDED_POOL_MAX);
        const diversityReminder = 'Keep a varied palette and mixed cover compositions.';

        if (count < RECOMMENDED_POOL_MIN) {
            guidance.dataset.tone = 'warning';
            guidance.textContent = `Pool has ${count} cover${count === 1 ? '' : 's'}; add ${missingCount} more to reach the recommended ${RECOMMENDED_POOL_MIN}-${RECOMMENDED_POOL_MAX}. ${diversityReminder}`;
            return;
        }

        if (count > RECOMMENDED_POOL_MAX) {
            guidance.dataset.tone = 'warning';
            guidance.textContent = `Pool has ${count} covers; trim ${overflowCount} to stay within the recommended ${RECOMMENDED_POOL_MIN}-${RECOMMENDED_POOL_MAX}. ${diversityReminder}`;
            return;
        }

        guidance.dataset.tone = 'success';
        guidance.textContent = `Pool size ${count} is within the recommended ${RECOMMENDED_POOL_MIN}-${RECOMMENDED_POOL_MAX}. ${diversityReminder}`;
    };

    const updateEmptyState = () => {
        empty.hidden = getCoverCards().length > 0;
    };

    const syncPreview = () => {
        const coverPaths = getCoverCards().map((card) => getCoverPath(card)).filter((coverPath) => {
            return coverPath !== '';
        });

        previewGrid.innerHTML = '';
        previewEmpty.hidden = coverPaths.length > 0;

        if (coverPaths.length === 0) {
            return;
        }

        for (let index = 0; index < PREVIEW_CELL_COUNT; index += 1) {
            const item = document.createElement('li');
            item.className = 'admin-ghost-preview-item';

            const image = document.createElement('img');
            image.src = coverPaths[index % coverPaths.length];
            image.alt = '';
            image.loading = 'lazy';
            image.decoding = 'async';
            image.draggable = false;

            item.append(image);
            previewGrid.append(item);
        }
    };

    const updateCardPositions = () => {
        const cards = getCoverCards();
        const canReorderByDrag = !mutationInFlight && cards.length > 1;

        cards.forEach((card, index) => {
            const position = card.querySelector('[data-ghost-cover-position]');
            if (position instanceof HTMLElement) {
                position.textContent = `#${index + 1}`;
            }

            card.draggable = canReorderByDrag;
            card.classList.toggle('is-draggable', canReorderByDrag);

            const moveUpButton = card.querySelector('[data-ghost-cover-move="up"]');
            if (moveUpButton instanceof HTMLButtonElement) {
                moveUpButton.disabled = mutationInFlight || index === 0;
            }

            const moveDownButton = card.querySelector('[data-ghost-cover-move="down"]');
            if (moveDownButton instanceof HTMLButtonElement) {
                moveDownButton.disabled = mutationInFlight || index === cards.length - 1;
            }

            const removeButton = card.querySelector('[data-ghost-cover-remove]');
            if (removeButton instanceof HTMLButtonElement) {
                removeButton.disabled = mutationInFlight;
            }
        });
    };

    const refreshGridMeta = () => {
        updateTotal();
        updateGuidance();
        updateEmptyState();
        updateCardPositions();
        syncPreview();
    };

    const setMutationInFlight = (nextValue) => {
        mutationInFlight = nextValue;
        syncAddButtons();
        updateCardPositions();
    };

    const beginMutation = () => {
        if (mutationInFlight) {
            return false;
        }

        setMutationInFlight(true);
        return true;
    };

    const endMutation = () => {
        setMutationInFlight(false);
    };

    const createSearchResultItem = (rawItem) => {
        if (!isRecord(rawItem)) {
            return null;
        }

        const templateRoot = searchResultTemplate.content.firstElementChild;
        if (!(templateRoot instanceof HTMLElement)) {
            return null;
        }

        const resultItem = templateRoot.cloneNode(true);
        if (!(resultItem instanceof HTMLElement)) {
            return null;
        }

        const igdbSourceKey = readString(rawItem.igdbSourceKey).trim();
        resultItem.dataset.igdbSourceKey = igdbSourceKey;

        const image = resultItem.querySelector('[data-ghost-search-cover]');
        if (image instanceof HTMLImageElement) {
            image.src = readString(rawItem.coverUrl);
        }

        const title = resultItem.querySelector('[data-ghost-search-title]');
        if (title instanceof HTMLElement) {
            title.textContent = readString(rawItem.title, 'Untitled');
        }

        const key = resultItem.querySelector('[data-ghost-search-key]');
        if (key instanceof HTMLElement) {
            key.textContent = `IGDB: ${igdbSourceKey}`;
        }

        const availabilityReason = readString(rawItem.availabilityReason).trim();
        const reason = resultItem.querySelector('[data-ghost-search-reason]');
        if (reason instanceof HTMLElement) {
            reason.hidden = availabilityReason === '';
            reason.textContent = availabilityReason;
        }

        const canAdd = rawItem.canAdd === true && igdbSourceKey !== '';
        const addButton = resultItem.querySelector('[data-ghost-search-add]');
        if (addButton instanceof HTMLButtonElement) {
            addButton.dataset.igdbSourceKey = igdbSourceKey;
            addButton.dataset.canAdd = canAdd ? '1' : '0';
            addButton.disabled = mutationInFlight || !canAdd;
            addButton.textContent = canAdd ? 'Add' : 'Unavailable';
        }

        return resultItem;
    };

    const createCoverCard = (rawItem) => {
        if (!isRecord(rawItem)) {
            return null;
        }

        const templateRoot = coverCardTemplate.content.firstElementChild;
        if (!(templateRoot instanceof HTMLElement)) {
            return null;
        }

        const card = templateRoot.cloneNode(true);
        if (!(card instanceof HTMLElement)) {
            return null;
        }

        card.dataset.ghostCoverId = readString(rawItem.id).trim();
        card.dataset.ghostCoverRemoveUrl = readString(rawItem.removeUrl).trim();

        const image = card.querySelector('[data-ghost-cover-image]');
        if (image instanceof HTMLImageElement) {
            image.src = readString(rawItem.coverPath);
        }

        return card;
    };

    const clearSearchResults = () => {
        searchResults.innerHTML = '';
    };

    const renderSearchResults = (items) => {
        clearSearchResults();

        items.forEach((item) => {
            const resultItem = createSearchResultItem(item);
            if (resultItem instanceof HTMLElement) {
                searchResults.append(resultItem);
            }
        });
    };

    const restoreOrder = (orderedIds) => {
        const cards = getCoverCards();
        const cardsById = new Map();

        cards.forEach((card) => {
            const cardId = String(card.dataset.ghostCoverId ?? '').trim();
            if (cardId !== '') {
                cardsById.set(cardId, card);
            }
        });

        const knownIds = new Set(orderedIds);
        const orderedCards = orderedIds.map((id) => cardsById.get(id)).filter((card) => {
            return card instanceof HTMLElement;
        });
        const remainingCards = cards.filter((card) => {
            const cardId = String(card.dataset.ghostCoverId ?? '').trim();
            return cardId === '' || !knownIds.has(cardId);
        });

        [...orderedCards, ...remainingCards].forEach((card) => {
            grid.append(card);
        });

        refreshGridMeta();
    };

    const persistOrder = async (fallbackOrderIds) => {
        const orderedIds = getCoverOrderIds();
        if (orderedIds.length === 0 || areOrdersEqual(fallbackOrderIds, orderedIds)) {
            return;
        }

        if (!beginMutation()) {
            return;
        }

        try {
            const result = await reorderGhostCovers({
                url: reorderUrl,
                csrfToken,
                orderedIds,
            });

            if (!result.ok) {
                throw new Error(result.errorMessage);
            }

            setSearchStatus('Cover order saved.', 'success');
        } catch (error) {
            restoreOrder(fallbackOrderIds);
            setSearchStatus(error instanceof Error ? error.message : 'Failed to save cover order.', 'error');
        } finally {
            endMutation();
        }
    };

    const performSearch = async (query) => {
        const sequence = ++searchSequence;
        setSearchStatus('Searching local catalog...', 'info');

        const result = await searchGhostCovers({
            url: searchUrl,
            query,
            limit: SEARCH_LIMIT,
        });

        if (sequence !== searchSequence) {
            return;
        }

        if (!result.ok || !isRecord(result.payload) || !Array.isArray(result.payload.results)) {
            clearSearchResults();
            setSearchStatus(result.errorMessage, 'error');
            return;
        }

        renderSearchResults(result.payload.results);

        if (result.payload.results.length === 0) {
            setSearchStatus('No matching local catalog titles found.', 'info');
            return;
        }

        setSearchStatus(
            `Found ${result.payload.results.length} local result${result.payload.results.length === 1 ? '' : 's'}.`,
            'success',
        );
    };

    const addCover = async (igdbSourceKey, triggerButton) => {
        if (igdbSourceKey === '' || triggerButton.dataset.canAdd !== '1') {
            return;
        }

        if (!beginMutation()) {
            return;
        }

        triggerButton.textContent = 'Adding...';
        setSearchStatus('Adding cover to ghost pool...', 'info');

        try {
            const result = await addGhostCover({
                url: addUrl,
                csrfToken,
                igdbSourceKey,
            });

            if (!result.ok || !isRecord(result.payload) || !isRecord(result.payload.item)) {
                throw new Error(result.errorMessage);
            }

            const coverCard = createCoverCard(result.payload.item);
            if (!(coverCard instanceof HTMLElement)) {
                throw new Error('Failed to render added ghost cover.');
            }

            grid.append(coverCard);
            refreshGridMeta();

            triggerButton.dataset.canAdd = '0';
            triggerButton.textContent = 'Added';
            setSearchStatus('Cover added to pool.', 'success');
        } catch (error) {
            triggerButton.dataset.canAdd = '1';
            triggerButton.textContent = 'Add';
            setSearchStatus(error instanceof Error ? error.message : 'Failed to add ghost cover.', 'error');
        } finally {
            endMutation();
            syncAddButtons();
        }
    };

    const removeCoverOptimistic = async (card, removeUrl) => {
        const parent = card.parentElement;
        const nextSibling = card.nextElementSibling;
        if (!(parent instanceof HTMLElement)) {
            return;
        }

        if (!beginMutation()) {
            return;
        }

        card.remove();
        refreshGridMeta();
        setSearchStatus('Removing cover...', 'info');

        try {
            const result = await removeGhostCover({
                url: removeUrl,
                csrfToken,
            });

            if (!result.ok) {
                throw new Error(result.errorMessage);
            }

            setSearchStatus('Cover removed.', 'success');
        } catch (error) {
            if (nextSibling instanceof HTMLElement && nextSibling.parentElement === parent) {
                parent.insertBefore(card, nextSibling);
            } else {
                parent.append(card);
            }

            refreshGridMeta();
            setSearchStatus(error instanceof Error ? error.message : 'Failed to remove cover.', 'error');
        } finally {
            endMutation();
        }
    };

    const clearDragState = () => {
        if (dragSourceCard instanceof HTMLElement) {
            dragSourceCard.classList.remove('is-dragging');
        }

        grid.classList.remove('is-drag-active');
        dragSourceCard = null;
        dragStartOrderIds = [];
        dragDidReorder = false;
    };

    searchInput.addEventListener('input', () => {
        const query = searchInput.value.trim();

        if (searchDebounceId !== null) {
            window.clearTimeout(searchDebounceId);
            searchDebounceId = null;
        }

        if (query.length < SEARCH_MIN_LENGTH) {
            searchSequence += 1;
            clearSearchResults();
            setSearchStatus('Type at least 2 characters to start searching.', 'info');
            return;
        }

        searchDebounceId = window.setTimeout(() => {
            searchDebounceId = null;
            void performSearch(query);
        }, SEARCH_DEBOUNCE_MS);
    });

    searchResults.addEventListener('click', (event) => {
        if (mutationInFlight) {
            return;
        }

        const target = event.target;
        if (!(target instanceof HTMLElement)) {
            return;
        }

        const addButton = target.closest('[data-ghost-search-add]');
        if (!(addButton instanceof HTMLButtonElement)) {
            return;
        }

        const igdbSourceKey = String(addButton.dataset.igdbSourceKey ?? '').trim();
        void addCover(igdbSourceKey, addButton);
    });

    grid.addEventListener('click', (event) => {
        if (mutationInFlight) {
            return;
        }

        const target = event.target;
        if (!(target instanceof HTMLElement)) {
            return;
        }

        const card = target.closest('.admin-ghost-cover-card');
        if (!(card instanceof HTMLElement)) {
            return;
        }

        const removeButton = target.closest('[data-ghost-cover-remove]');
        if (removeButton instanceof HTMLButtonElement) {
            const removeUrl = String(card.dataset.ghostCoverRemoveUrl ?? '').trim();
            if (removeUrl === '') {
                return;
            }

            void removeCoverOptimistic(card, removeUrl);
            return;
        }

        const moveButton = target.closest('[data-ghost-cover-move]');
        if (!(moveButton instanceof HTMLButtonElement)) {
            return;
        }

        const direction = String(moveButton.dataset.ghostCoverMove ?? '').trim();
        if (direction !== 'up' && direction !== 'down') {
            return;
        }

        const previousOrder = getCoverOrderIds();

        const sibling = direction === 'up' ? card.previousElementSibling : card.nextElementSibling;
        if (!(sibling instanceof HTMLElement)) {
            return;
        }

        if (direction === 'up') {
            grid.insertBefore(card, sibling);
        } else {
            grid.insertBefore(sibling, card);
        }

        refreshGridMeta();
        void persistOrder(previousOrder);
    });

    grid.addEventListener('dragstart', (event) => {
        if (mutationInFlight) {
            event.preventDefault();
            return;
        }

        const target = event.target;
        if (!(target instanceof HTMLElement)) {
            return;
        }

        const card = target.closest('.admin-ghost-cover-card');
        if (!(card instanceof HTMLElement) || !card.draggable) {
            event.preventDefault();
            return;
        }

        dragSourceCard = card;
        dragStartOrderIds = getCoverOrderIds();
        dragDidReorder = false;
        card.classList.add('is-dragging');
        grid.classList.add('is-drag-active');

        if (event.dataTransfer !== null) {
            event.dataTransfer.effectAllowed = 'move';
            event.dataTransfer.setData('text/plain', String(card.dataset.ghostCoverId ?? '').trim());
        }
    });

    grid.addEventListener('dragover', (event) => {
        if (!(dragSourceCard instanceof HTMLElement) || mutationInFlight) {
            return;
        }

        event.preventDefault();
        if (event.dataTransfer !== null) {
            event.dataTransfer.dropEffect = 'move';
        }

        const target = event.target;
        if (!(target instanceof HTMLElement)) {
            return;
        }

        const overCard = target.closest('.admin-ghost-cover-card');
        if (!(overCard instanceof HTMLElement) || overCard === dragSourceCard) {
            return;
        }

        const rect = overCard.getBoundingClientRect();
        const shouldInsertAfter = event.clientY > rect.top + (rect.height / 2);
        if (shouldInsertAfter) {
            grid.insertBefore(dragSourceCard, overCard.nextElementSibling);
        } else {
            grid.insertBefore(dragSourceCard, overCard);
        }

        dragDidReorder = true;
        updateCardPositions();
    });

    grid.addEventListener('drop', (event) => {
        if (!(dragSourceCard instanceof HTMLElement)) {
            return;
        }

        event.preventDefault();
    });

    grid.addEventListener('dragend', () => {
        if (!(dragSourceCard instanceof HTMLElement)) {
            clearDragState();
            return;
        }

        const previousOrder = [...dragStartOrderIds];
        const didReorder = dragDidReorder;
        const nextOrder = getCoverOrderIds();

        clearDragState();
        refreshGridMeta();

        if (!didReorder || areOrdersEqual(previousOrder, nextOrder)) {
            return;
        }

        void persistOrder(previousOrder);
    });

    refreshGridMeta();
    syncAddButtons();
}

function readString(value, fallback = '') {
    return typeof value === 'string' ? value : fallback;
}
